import { useState } from 'react';
import {
  ToastStatus,
  useDelayCall,
  useIsMsalAuth,
  useStickySafeNavigate,
} from '@equifinance/shared';
import useAccountApi, {
  defaultTransitionStatus,
  TransitionForward,
  TransitionStageContext,
  TransitionStatus,
} from '../../api/useAccountApi';
import useTransitionResult from './useTransitionResult';
import { ErrorDisplayType } from './Account';

export function useTransition(
  fetchAccount: (incrementUpdateCount?: boolean) => Promise<void>,
  fetchProductsSummary: () => Promise<void>,
  accountId?: string,
) {
  const [transitionStatus, setTransitionStatus] = useState<TransitionStatus>(defaultTransitionStatus);

  const { transition, getTransitionStatus } = useAccountApi();
  const isMsalAuth = useIsMsalAuth();
  const contactTeam = isMsalAuth ? 'Equifinance IT Team' : 'Equifinance';

  const navigate = useStickySafeNavigate();

  const {
    showSuccessModal,
    showWarningModal,
    showFailureModal,
    showTransitionToast,
  } = useTransitionResult();

  const handleTransitionStatus = (state: TransitionStatus) => {
    const { reason, transitionLabel } = state;
    if (!reason) return;

    const { errors = [], warnings = [] } = reason;
    const validErrors = errors?.filter((e) => e?.messages?.length);
    const validWarnings = warnings?.filter((e) => e?.messages?.length);
    const action = transitionLabel;
    const hasErrors = !!validErrors?.length;
    const hasWarnings = !!validWarnings?.length;

    if (hasErrors) {
      const title = `${action} errors`;
      const label = `${action} can't be undertaken, please see details below:`;
      const displayType = validErrors[0]?.displayType;
      if (displayType === ErrorDisplayType.Modal) {
        showFailureModal(title, label, validErrors);
      } else if (displayType === ErrorDisplayType.Toast) {
        showTransitionToast(ToastStatus.ERROR, label, validErrors);
      }
    }

    if (hasWarnings) {
      const label = `${action} can be undertaken. However there are warnings to be aware of, please see details below:`;
      const title = `${action} warnings`;
      const displayType = validWarnings[0]?.displayType;
      if (displayType === ErrorDisplayType.Modal) {
        showWarningModal(title, label, validWarnings);
      } else if (displayType === ErrorDisplayType.Toast) {
        showTransitionToast(ToastStatus.WARNING, label, validWarnings);
      }
    }
  };

  const handleTransitionResponse = (result: TransitionForward) => {
    const { actionResult } = result;
    const { errors = [], warnings = [] } = actionResult.reason;
    const validErrors = errors?.filter((e) => e?.messages?.length);
    const validWarnings = warnings?.filter((e) => e?.messages?.length);
    const action = transitionStatus.transitionLabel;
    const hasErrors = validErrors.length > 0;
    const hasWarnings = validWarnings.length > 0;
    const errorDisplayType = validErrors[0]?.displayType;
    const warningDisplayType = validWarnings[0]?.displayType;

    setTransitionStatus(result.transitionState);

    const callback = () => {
      setTimeout(() => {
        handleTransitionStatus(result.transitionState);
      }, 50);
    };

    if (actionResult.transitionSuccessful) {
      const title = `${transitionStatus.transitionLabel} success`;

      if (!hasErrors && !hasWarnings) {
        showSuccessModal(title, `${title}.`, [], callback);
        return;
      }

      const label = `${action} completed with warnings, please see details below:`;

      if (warningDisplayType === ErrorDisplayType.Modal) {
        showWarningModal(title, label, validWarnings, callback);
        return;
      }
      if (warningDisplayType === ErrorDisplayType.Toast) {
        showTransitionToast(ToastStatus.WARNING, label, validWarnings);
      }
      callback();

      return;
    }

    const title = `${action} failure`;
    const label = `${action} failure, please see details below. If the issue persists please contact ${contactTeam} for further support:`;

    if (errorDisplayType === ErrorDisplayType.Modal) {
      showFailureModal(title, label, validErrors, callback);
      return;
    }
    if (errorDisplayType === ErrorDisplayType.Toast) {
      showTransitionToast(ToastStatus.ERROR, label, validErrors);
    }
    callback();
  };

  const fetchTransitionStatus = async () => {
    if (accountId) {
      const transitionStatusResult = await getTransitionStatus(accountId) || defaultTransitionStatus;
      setTransitionStatus(transitionStatusResult);
      handleTransitionStatus(transitionStatusResult);
    }
  };

  useDelayCall(() => {
    (async () => {
      await fetchTransitionStatus();
    })();
  }, [accountId], true);

  const makeTransition = async (stageContext?: TransitionStageContext) => {
    const result = await transition(accountId ?? '', {
      hasConsent: stageContext?.hasConsent || false,
      readyForServicing: stageContext?.readyForServicing || false,
    });
    handleTransitionResponse(result);
    if (result?.actionResult?.transitionSuccessful) {
      await fetchAccount();
      await fetchProductsSummary();
      navigate(`application/${accountId}`);
    }
  };

  return { transitionStatus, fetchTransitionStatus, makeTransition };
}
